<script setup>
/**
 * The invitation card at a page seam of the Lessons feed
 * (Claude outputs/community-feed-invite-2026-10-30/DESIGN.md §4.1, §4.6 to §4.8).
 *
 * Dark between the cream lesson cards, and quiet: the Mystic's square
 * portrait and name (one button, "Meet {name}": the room), the count line
 * ("Three lessons about work in a row."), the title ("A work question of your
 * own?"), and "✦ Ask {name}" (the room, then the reading, its box empty).
 *
 * The first time it is 40% in view it draws in (.pre → .in), once; under
 * reduced motion, or without an IntersectionObserver, it is simply there.
 * Emits `reveal` (drawn in), `shown` (50% in view for 1 second, once), `past` (shown, then wholly
 * above the scroll pane), `meet` and `ask`.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { INVITE_TOPIC, INVITE_ASK, INVITE_MEET, inviteCount } from '@/utils/community-lessons'
import { RULES } from '@/utils/lesson-invite'
import { reducedMotionNow } from '@/composables/useReducedMotion'

const props = defineProps({
  card: { type: Object, required: true },
  // The scroll container the card moves in (the Lessons pane); null = the viewport.
  root: { type: Function, default: () => null },
  // Already drawn in once (the stream came back after a search): no second reveal.
  revealed: { type: Boolean, default: false },
})
const emit = defineEmits(['reveal', 'shown', 'past', 'meet', 'ask'])

const el = ref(null)
const hasIO = typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function'
const motion = hasIO && !reducedMotionNow()
const phase = ref(motion && !props.revealed ? 'pre' : '')
const failed = ref(false)

const name = computed(() => props.card.name || 'Mystic Aida')
const words = computed(() => INVITE_TOPIC[props.card.topic] || { title: '' })
const count = computed(() => inviteCount(props.card))
const titleId = computed(() => `lesson-invite-h-${props.card.seam}`)
const initial = computed(() => String(name.value || '✦').trim().charAt(0).toUpperCase())

let io = null
let seenTimer = null
let seen = false
function onEntries(entries) {
  entries.forEach((e) => {
    const r = e.intersectionRatio
    if (r >= RULES.REVEAL_RATIO && phase.value === 'pre') { phase.value = 'in'; emit('reveal') }
    if (r >= RULES.SEEN_RATIO && !seen && !seenTimer) {
      seenTimer = setTimeout(() => { seenTimer = null; seen = true; emit('shown') }, RULES.SEEN_MS)
    }
    if (r < RULES.SEEN_RATIO && seenTimer) { clearTimeout(seenTimer); seenTimer = null }
    if (!e.isIntersecting && seen) {
      const top = e.rootBounds ? e.rootBounds.top : 0
      if (e.boundingClientRect && e.boundingClientRect.bottom <= top) emit('past')
    }
  })
}
onMounted(() => {
  if (!hasIO || !el.value) return
  try {
    io = new window.IntersectionObserver(onEntries, { root: props.root() || null, threshold: [0, RULES.REVEAL_RATIO, RULES.SEEN_RATIO] })
    io.observe(el.value)
  } catch (e) { io = null; phase.value = '' }
})
onBeforeUnmount(() => {
  if (seenTimer) clearTimeout(seenTimer)
  if (io) { try { io.disconnect() } catch (e) { /* */ } }
})
defineExpose({ el })
</script>

<template>
  <aside ref="el" class="iv" :class="phase" :aria-labelledby="titleId" :data-seam="card.seam" data-testid="lessons-invite-card">
    <span class="iv-line" aria-hidden="true"></span>
    <button type="button" class="iv-who" :aria-label="INVITE_MEET(name)" data-testid="lessons-invite-meet" @click="emit('meet')">
      <span class="iv-sq">
        <img v-if="card.avatar && !failed" :src="card.avatar" alt="" @error="failed = true" />
        <span v-else class="iv-ph" aria-hidden="true">{{ initial }}</span>
      </span>
      <span class="iv-nm">{{ name }}</span>
    </button>
    <div class="iv-t">
      <p class="iv-count">{{ count }}</p>
      <h2 :id="titleId" class="iv-h">{{ words.title }}</h2>
    </div>
    <button type="button" class="iv-ask" data-testid="lessons-invite-ask" @click="emit('ask')"><span class="iv-star" aria-hidden="true">✦</span> {{ INVITE_ASK(name) }}</button>
  </aside>
</template>

<style scoped>
.iv {
  --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --display: 'Letter Display', 'Cinzel', Georgia, serif;
  --ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  grid-column: 1 / -1; position: relative; box-sizing: border-box; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 26px;
  padding: 24px 30px; border-radius: 18px; overflow: hidden; text-align: left; font-family: var(--ui);
  background: radial-gradient(120% 140% at 0% 0%, #2c1658 0%, #170a31 55%, #100624 100%); border: 1px solid rgba(231, 201, 131, .26);
  box-shadow: 0 24px 46px -26px rgba(0, 0, 0, .95);
}
.iv *, .iv *::before, .iv *::after { box-sizing: border-box; }
.iv-line { position: absolute; left: 0; right: 0; top: 0; height: 1px; transform-origin: 50% 50%;
  background: linear-gradient(90deg, transparent, #e7c983 30%, #ffe9b8 50%, #e7c983 70%, transparent); }
.iv-who { display: flex; flex-direction: column; align-items: center; gap: 8px; width: 104px; min-height: 44px; padding: 4px 0; border: 0; background: none; cursor: pointer;
  color: inherit; border-radius: 12px; font: inherit; }
.iv-sq { display: block; width: 84px; height: 84px; border-radius: 14px; overflow: hidden; border: 1px solid rgba(231, 201, 131, .7);
  box-shadow: 0 0 0 4px rgba(231, 201, 131, .08), 0 14px 26px -12px #000; background: #2a1845; transition: border-color .2s; }
.iv-sq img { display: block; width: 100%; height: 100%; object-fit: cover; }
.iv-ph { display: grid; place-items: center; width: 100%; height: 100%; font: 600 30px/1 var(--display); color: #e7c983; }
.iv-nm { font: italic 600 16px/1.15 var(--serif); color: #f0d9a3; text-align: center; overflow-wrap: anywhere;
  text-decoration: underline; text-decoration-color: rgba(240, 217, 163, .35); text-underline-offset: 3px; }
.iv-who:hover .iv-nm { text-decoration-color: #f0d9a3; }
.iv-who:hover .iv-sq { border-color: #ffe9b8; }
.iv-t { min-width: 0; }
.iv-count { margin: 0 0 6px; font: 500 15px/1.4 var(--ui); color: #c9b8e6; }
.iv-h { margin: 0; font: 600 30px/1.1 var(--serif); color: #fff6e4; text-wrap: balance; }
/* Today's .lx-btn: the 52px pill. */
.iv-ask {
  display: inline-flex; align-items: center; justify-content: center; gap: 9px; min-height: 52px; padding: 14px 28px; border-radius: 999px; white-space: nowrap;
  border: 1px solid rgba(240, 217, 163, .55); background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #ffdfa3;
  font: 700 15px var(--ui); box-shadow: 0 12px 30px -8px rgba(78, 39, 118, .8); cursor: pointer; max-width: 100%;
}
.iv-ask:hover { filter: brightness(1.08); }
.iv-star { display: inline-block; }
.iv-who:focus-visible, .iv-ask:focus-visible { outline: 2px solid #e7c983; outline-offset: 2px; }

/* The reveal (§4.7): once, the first time it is 40% in view. */
.iv.pre .iv-line { transform: scaleX(0); }
.iv.pre .iv-sq, .iv.pre .iv-nm, .iv.pre .iv-count, .iv.pre .iv-h, .iv.pre .iv-ask { opacity: 0; }
.iv.in .iv-line { animation: iv-draw .7s cubic-bezier(.2, .8, .2, 1) both; }
.iv.in .iv-sq { animation: iv-face .6s cubic-bezier(.2, .8, .2, 1) .12s both; }
.iv.in .iv-count { animation: iv-up .5s ease .2s both; }
.iv.in .iv-nm { animation: iv-up .5s ease .26s both; }
.iv.in .iv-h { animation: iv-up .55s ease .3s both; }
.iv.in .iv-ask { animation: iv-up .5s ease .42s both; }
.iv.in .iv-star { animation: iv-twinkle .9s cubic-bezier(.2, .8, .2, 1) .8s both; }
@keyframes iv-draw { from { transform: scaleX(0); } to { transform: scaleX(1); } }
@keyframes iv-face { from { opacity: 0; transform: scale(.9); filter: blur(3px); } to { opacity: 1; transform: none; filter: none; } }
@keyframes iv-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@keyframes iv-twinkle { 0% { transform: none; } 50% { transform: rotate(90deg) scale(1.4); color: #fff3c4; } 100% { transform: rotate(180deg); } }

@media (max-width: 767px) {
  .iv { grid-template-columns: minmax(0, 1fr); justify-items: center; text-align: center; gap: 14px; padding: 24px 20px 22px; }
  .iv-who { width: auto; }
  .iv-sq { width: 72px; height: 72px; }
  .iv-h { font-size: 27px; }
  .iv-count { font-size: 14.5px; }
  .iv-ask { width: 100%; max-width: 320px; white-space: normal; }
}
@media (prefers-reduced-motion: reduce) {
  .iv .iv-line, .iv .iv-sq, .iv .iv-nm, .iv .iv-count, .iv .iv-h, .iv .iv-ask, .iv .iv-star {
    animation: none !important; opacity: 1 !important; transform: none !important; filter: none !important; transition: none;
  }
}
</style>
